import React from 'react';
import ReactDOM from 'react-dom/client';

import { QueryClientProvider } from 'react-query';

import { queryClientMobile } from '../api/queryClient';
import { setLocaleApplier } from '../contexts/localeBridge';

import { chargerLangue, langueDeDepart } from './i18n/langue';

import './mobile.css';
import Shell from './shell/Shell';

/**
 * Point d'entrée de l'app mobile (m.trigger-flow.com), distinct de src/main.tsx.
 *
 * **Seul `QueryClientProvider` est monté ici.** `ContextProvider` amorce une
 * session authentifiée dès le montage, et cette coque doit encore s'afficher pour
 * un visiteur anonyme — c'est même son seul métier hors standalone (#21 : « m.
 * hors standalone ne montre aucun formulaire de connexion, seulement un guide
 * d'installation »). Le câbler ici déclencherait un 401 en arrière-plan sur une
 * page qui n'a rien à authentifier, avec le risque d'éjection qui va avec.
 *
 * C'est donc `Shell` qui décide de le monter, une fois le jeton constaté (#75).
 * Le client de requêtes, lui, n'appelle rien tout seul : il peut vivre au-dessus.
 *
 * Absents par rapport à `src/main.tsx`, et volontairement : `I18nextProvider`
 * (le mobile a son propre dictionnaire, `i18n/langue.ts`) et `PostHogProvider`.
 */
// La langue arrive par `/user` (`ContextProvider` → `applyLocale`) : celle de l'app
// (`app_locale`, réglable par la gouvernante), avec le dictionnaire du mobile,
// jamais avec `src/i18n.ts` (#99).
setLocaleApplier((locale, appLocale) => {
    void chargerLangue(appLocale ?? locale);
});

// Premier rendu dans la langue du dernier lancement (ou du téléphone) : pas
// d'éclair de français avant `/user`. Un chargement en échec rend quand même.
void chargerLangue(langueDeDepart()).finally(() => {
    ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
        <React.StrictMode>
            <QueryClientProvider client={queryClientMobile}>
                <Shell />
            </QueryClientProvider>
        </React.StrictMode>,
    );
});

// Le SW porte le push (#20) ET la coque précachée (#86). Son enregistrement ne
// conditionne pas l'affichage : un échec doit laisser la coque lisible, jamais la
// remplacer par une erreur.
//
// ⚠️ `?v=` n'est pas décoratif — c'est TOUTE la stratégie de version de cache.
//
// `sw-mobile.js` vit dans `public/`, donc Vite ne le transforme pas et on ne peut
// pas y injecter la version de build. Mais le navigateur compare l'URL du script
// octet par octet : une version différente est un NOUVEAU service worker, donc
// `install` se déclenche, le nouveau cache se remplit et l'ancien est supprimé à
// l'activation. Le SW relit cette même version dans `self.location.search`.
//
// Retirer ce paramètre ferait vivre les utilisateurs sur la coque du déploiement
// précédent, indéfiniment et sans erreur.
if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
        navigator.serviceWorker.register(`/sw-mobile.js?v=${__APP_BUILD_VERSION__}`).catch(() => {
            /* pas d'écran d'erreur pour ça : la coque reste utile sans push */
        });
    });
}
